import { useState } from "react";
import { ShieldAlert, ShieldCheck, Search, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fetchAnalyse, fetchProcedures } from "@/lib/entreprises";
import { fetchHolding, setHolding } from "@/lib/reports";
import DirigeantCard from "@/components/pappers/DirigeantCard";
import ProceduresBlock from "@/components/pappers/ProceduresBlock";
import { GroupeHoldingBlock, MandatsProceduresTable } from "@/components/pappers/GroupeBlocks";
import GroupeSynthese from "@/components/pappers/GroupeSynthese";

export default function AnalyseDirigeantTab({ dossierId, siren, nomEntreprise }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [checks, setChecks] = useState({});
    const [holding, setHoldingState] = useState(null);

    async function run() {
        setLoading(true);
        setError("");
        try {
            setData(await fetchAnalyse(siren, dossierId));
            if (dossierId) fetchHolding(dossierId).then((h) => setHoldingState(h.holding)).catch(() => {});
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }

    async function defineHolding(s) {
        try {
            const isSame = holding?.siren === s?.siren;
            const r = await setHolding(dossierId, isSame ? null : s.siren, isSame ? null : s.nom_entreprise);
            setHoldingState(r.holding);
            toast.success(isSame ? "Société mère forcée retirée" : `${s.nom_entreprise} définie comme Holding (société mère)`);
            setData(await fetchAnalyse(siren, dossierId));
        } catch (e) { toast.error("Modification impossible", { description: e.message }); }
    }

    async function verify(s) {
        setChecks((c) => ({ ...c, [s]: { loading: true } }));
        try {
            const p = await fetchProcedures(s);
            setChecks((c) => ({ ...c, [s]: p }));
        } catch (e) {
            setChecks((c) => ({ ...c, [s]: { error: e.message } }));
            toast.error("Vérification impossible", { description: e.message });
        }
    }

    if (!data) {
        return (
            <Card data-testid="analyse-intro" className="border-dashed border-border bg-transparent shadow-sm">
                <CardContent className="py-14 flex flex-col items-center text-center">
                    <div className="h-14 w-14 rounded-full bg-accent flex items-center justify-center mb-5">
                        <Search className="h-6 w-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <h2 className="font-display text-2xl text-foreground">Analyse croisée Pappers</h2>
                    <p className="mt-2 max-w-lg text-sm text-muted-foreground">
                        Procédures collectives de {nomEntreprise}, et pour chaque dirigeant : ses autres mandats, sociétés
                        actives ou cessées. Consomme des jetons Pappers (résultats mis en cache 30 jours).
                    </p>
                    {error && (
                        <p data-testid="analyse-error" className="mt-4 flex items-center gap-2 text-sm text-destructive">
                            <AlertTriangle className="h-4 w-4" /> {error}
                        </p>
                    )}
                    <Button data-testid="run-analyse-button" onClick={run} disabled={loading} className="mt-8 rounded-full h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" strokeWidth={2} />}
                        {loading ? "Analyse en cours…" : "Lancer l'analyse"}
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const ent = data.entreprise;
    const alert = ent.procedure_en_cours || ent.procedures.length > 0 || ent.entreprise_cessee;

    return (
        <div data-testid="analyse-result" className="space-y-8">
            <Card data-testid="entreprise-procedures-card" className={`shadow-sm ${alert ? "border-amber-300 bg-amber-50/50" : "border-emerald-200 bg-emerald-50/40"}`}>
                <CardContent className="pt-6 space-y-4">
                    <div className="flex items-center gap-3">
                        {alert ? <ShieldAlert className="h-5 w-5 text-amber-700" /> : <ShieldCheck className="h-5 w-5 text-emerald-700" />}
                        <h2 className="font-display text-xl text-foreground">{data.nom_entreprise} — situation juridique</h2>
                        <Button variant="ghost" size="sm" data-testid="refresh-analyse-button" onClick={run} disabled={loading} className="ml-auto rounded-full text-muted-foreground">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Actualiser"}
                        </Button>
                    </div>
                    <ProceduresBlock data={ent} testId="entreprise-procedures" />
                    <div data-testid="convention-collective" className="text-sm">
                        <span className="text-muted-foreground">Convention collective (K-bis / Pappers) : </span>
                        {ent.conventions_collectives?.length ? ent.conventions_collectives.map((c) => (
                            <Badge key={`${c.idcc}-${c.nom}`} className="mr-1.5 rounded-full bg-primary/10 text-primary border-transparent shadow-sm">{c.nom || "n/c"}{c.idcc ? ` · IDCC ${c.idcc}` : ""}</Badge>
                        )) : <span className="text-muted-foreground">non renseignée</span>}
                    </div>
                </CardContent>
            </Card>

            <GroupeSynthese nomEntreprise={data.nom_entreprise} groupe={data.groupe} checks={checks} />
            <GroupeHoldingBlock groupe={data.groupe} />
            <MandatsProceduresTable groupe={data.groupe} checks={checks} onVerify={verify} onDefineHolding={dossierId ? defineHolding : undefined} holdingSiren={holding?.siren || data.groupe?.societe_mere?.siren} />

            <section className="space-y-4">
                <h2 className="font-display text-2xl text-foreground">
                    Dirigeants <span className="italic text-muted-foreground">et mandats croisés</span>
                </h2>
                {data.dirigeants.length === 0 ? (
                    <p data-testid="no-dirigeant" className="text-sm text-muted-foreground">Aucun dirigeant personne physique renseigné chez Pappers.</p>
                ) : (
                    data.dirigeants.map((d) => <DirigeantCard key={d.nom_complet} dirigeant={d} checks={checks} onVerify={verify} />)
                )}
            </section>
        </div>
    );
}
